import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Camera, Smartphone, Shield, Clock } from 'lucide-react';

const SecuritySection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      image: "https://customer-assets.emergentagent.com/job_web-clone-project-10/artifacts/6q690dj0_17262379778614.webp",
      alt: "Sistema CFTV Intelbras"
    },
    {
      id: 2,
      image: "https://customer-assets.emergentagent.com/job_web-clone-project-10/artifacts/gfxa0k0f_AF-Aplicativo-Guardian-590x360px-02.jpg",
      alt: "Aplicativo Guardian Intelbras"
    },
    {
      id: 3,
      image: "https://customer-assets.emergentagent.com/job_web-clone-project-10/artifacts/ymr5ozok_melhor-camera-intelbras.webp",
      alt: "Câmeras Intelbras"
    },
    {
      id: 4,
      image: "https://customer-assets.emergentagent.com/job_web-clone-project-10/artifacts/44kmzvif_praticidade-e-eficiencia-intelbras-guardian.jpg",
      alt: "App Intelbras Guardian"
    }
  ];

  const benefits = [
    {
      icon: Camera,
      title: "Alta Resolução",
      description: "Câmeras com imagens nítidas em Full HD"
    },
    {
      icon: Smartphone,
      title: "Acesso Remoto",
      description: "Monitore pelo celular de qualquer lugar"
    },
    {
      icon: Shield,
      title: "Armazenamento Local",
      description: "Gravações seguras em HD ou DVR"
    },
    {
      icon: Clock,
      title: "Gravação 24h",
      description: "Monitoramento contínuo e confiável"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Instalação de Câmeras de Segurança
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Proteja seu lar ou empresa com nosso sistema completo de monitoramento CFTV Intelbras
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
          {/* Carousel */}
          <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={slide.image}
                  alt={slide.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 p-3 rounded-full transition-all z-10"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 p-3 rounded-full transition-all z-10"
            >
              <ChevronRight size={24} />
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentSlide ? 'bg-orange-500 w-8' : 'bg-white bg-opacity-50'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-3xl font-bold text-gray-800 mb-6">
              Sistema Completo de Monitoramento
            </h3>
            <p className="text-lg text-gray-600 mb-8">
              Oferecemos instalação profissional de câmeras de segurança Intelbras com tecnologia de ponta. 
              Monitore sua propriedade 24 horas por dia através do aplicativo no seu celular, 
              com armazenamento local seguro e imagens de alta qualidade.
            </p>

            {/* Benefits Grid */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="flex items-start">
                    <div className="bg-orange-500 p-3 rounded-lg mr-3 flex-shrink-0">
                      <Icon className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1">{benefit.title}</h4>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <a
              href="https://api.whatsapp.com/send?phone=5588996105011&text=Olá%20gostaria%20de%20um%20orçamento%20para%20instalação%20de%20câmeras%20de%20segurança"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-8 py-4 rounded-lg text-lg font-bold hover:from-orange-600 hover:to-yellow-500 transition-all hover:scale-105 shadow-lg"
            >
              Solicitar Orçamento
            </a>
          </div>
        </div>

        {/* Features Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-orange-100">
            <div className="text-4xl mb-4">📱</div>
            <h4 className="text-xl font-bold text-gray-800 mb-3">App Intelbras Guardian</h4>
            <p className="text-gray-600">
              Controle total pelo celular. Veja suas câmeras em tempo real, receba notificações e gerencie gravações.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-orange-100">
            <div className="text-4xl mb-4">🛡️</div>
            <h4 className="text-xl font-bold text-gray-800 mb-3">Instalação Profissional</h4>
            <p className="text-gray-600">
              Equipe técnica especializada realiza toda a instalação com garantia de qualidade e funcionamento.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-orange-100">
            <div className="text-4xl mb-4">💾</div>
            <h4 className="text-xl font-bold text-gray-800 mb-3">Armazenamento Seguro</h4>
            <p className="text-gray-600">
              Suas gravações ficam armazenadas localmente em HD ou DVR, sem mensalidades de nuvem.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecuritySection;
